import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
  Popconfirm,
  Tag,
  Alert,
  Row,
  Col,
  Statistic,
  Badge,
  Drawer,
  List,
  Avatar,
  Tooltip,
  Divider,
  Dropdown,
  MenuProps,
  Descriptions,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BuildOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  StopOutlined,
  TeamOutlined,
  UserAddOutlined,
  MailOutlined,
  CrownOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SendOutlined,
  CopyOutlined,
  HomeOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../../contexts/organization-context";
import apiClient from "../../utils/axios";
import { BillingImpactModal } from "../../components/BillingImpactModal";
import { FacilityBillingService } from "../../services/facilityBillingService";

const { Title, Text } = Typography;
const { Option } = Select;

// Phone number utilities
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Handle different digit lengths
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  
  return phoneNumber; // Return original if it doesn't match expected patterns
};

const stripPhoneFormatting = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');
  
  // Remove leading '1' if it's an 11-digit number
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  
  return digits;
};

// US States list
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

interface Facility {
  id: string;
  name: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  facility_status: 'Active' | 'Inactive';
  created_at: string;
  active_users_count?: number;
  total_members_count?: number;
}

interface FacilityFormData {
  name: string;
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  phone?: string;
  facility_status: 'Active' | 'Inactive';
}

interface FacilityMember {
  id: string;
  user_id: string;
  name: string;
  email: string;
  role: 'Owner' | 'Member';
  status: string;
  created_at: string;
  last_login?: string;
}

interface MembersData {
  members: FacilityMember[];
  total_count: number;
  pending_invitations: number;
}

export const Facilities: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  
  // Member management state
  const [membersDrawerVisible, setMembersDrawerVisible] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [membersData, setMembersData] = useState<MembersData | null>(null);
  const [membersLoading, setMembersLoading] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [inviteForm] = Form.useForm();
  const [inviting, setInviting] = useState(false);
  
  // Billing impact modal state
  const [billingModalVisible, setBillingModalVisible] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'create' | 'delete' | 'status_change';
    facilityId?: string;
    newStatus?: 'Active' | 'Inactive';
    formData?: any;
  } | null>(null);

  const { getAccessTokenSilently } = useAuth0();
  const { ownedOrganizations, isOrganizationOwner } = useOrganization();

  const fetchFacilities = async () => {
    if (!isOrganizationOwner || ownedOrganizations.length === 0) return;

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get(`/api/organization/${ownedOrganizations[0].id}/facilities`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setFacilities(response.data);
    } catch (error) {
      console.error("Error fetching facilities:", error);
      message.error("Failed to load facilities");
    } finally {
      setLoading(false);
    }
  };

  const fetchFacilityMembers = async (facilityId: string) => {
    try {
      setMembersLoading(true);
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get(`/api/facility/${facilityId}/members`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setMembersData(response.data);
    } catch (error) {
      console.error("Error fetching facility members:", error);
      message.error("Failed to load facility members");
    } finally {
      setMembersLoading(false);
    }
  };

  const handleManageMembers = (facility: Facility) => {
    setSelectedFacility(facility);
    setMembersDrawerVisible(true);
    fetchFacilityMembers(facility.id);
  };

  const handleInviteMember = async (values: any) => {
    if (!selectedFacility) return;

    try {
      setInviting(true);
      const token = await getAccessTokenSilently();
      
      // Parse multiple emails from the textarea
      const emailText = values.emails || '';
      const emails = emailText
        .split(/[,;\n\r]+/) // Split by comma, semicolon, or newlines
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);

      if (emails.length === 0) {
        message.error('Please enter at least one email address');
        return;
      }

      // Validate each email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter((email: string) => !emailRegex.test(email));
      
      if (invalidEmails.length > 0) {
        message.error(`Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`);
        return;
      }

      // Send invitations for each email
      let successCount = 0;
      let failedEmails: string[] = [];

      for (const email of emails) {
        try {
          await apiClient.post(`/api/facility/${selectedFacility.id}/members/invite`, {
            email: email,
            role: 'Member' // Always use Member role
          }, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          successCount++;
        } catch (error: any) {
          console.error(`Error sending invitation to ${email}:`, error);
          failedEmails.push(email);
        }
      }

      // Show results
      if (successCount > 0) {
        message.success(`Successfully sent ${successCount} invitation${successCount > 1 ? 's' : ''}`);
      }
      
      if (failedEmails.length > 0) {
        message.error(`Failed to send invitations to: ${failedEmails.join(', ')}`);
      }

      if (successCount > 0) {
        setInviteModalVisible(false);
        inviteForm.resetFields();
        
        // Refresh both member list and facilities table
        await Promise.all([
          fetchFacilityMembers(selectedFacility.id),
          fetchFacilities()
        ]);
      }
    } catch (error: any) {
      console.error("Error sending invitations:", error);
      message.error("Failed to send invitations");
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvitation = async (memberId: string, memberEmail: string) => {
    if (!selectedFacility) return;

    try {
      const token = await getAccessTokenSilently();
      
      await apiClient.post(`/api/facility/${selectedFacility.id}/members/${memberId}/resend-invitation`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      message.success(`Invitation resent to ${memberEmail}`);
      
      // Refresh both member list and facilities table
      await Promise.all([
        fetchFacilityMembers(selectedFacility.id),
        fetchFacilities()
      ]);
    } catch (error: any) {
      console.error("Error resending invitation:", error);
      message.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const handleCopyInvitationLink = async (memberId: string, memberEmail: string) => {
    if (!selectedFacility) return;

    try {
      const token = await getAccessTokenSilently();
      
      const response = await apiClient.get(`/api/facility/${selectedFacility.id}/members/${memberId}/invitation-url`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      const invitationUrl = response.data.invitation_url;
      
      // Copy to clipboard
      await navigator.clipboard.writeText(invitationUrl);
      message.success(`Invitation link copied to clipboard for ${memberEmail}`);
    } catch (error: any) {
      console.error("Error getting invitation link:", error);
      message.error(error.response?.data?.detail || "Failed to get invitation link");
    }
  };

  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (!selectedFacility) return;

    try {
      const token = await getAccessTokenSilently();
      
      await apiClient.delete(`/api/facility/${selectedFacility.id}/members/${memberId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      message.success(`${memberName} removed from facility`);
      
      // Refresh both member list and facilities table
      await Promise.all([
        fetchFacilityMembers(selectedFacility.id),
        fetchFacilities()
      ]);
    } catch (error: any) {
      console.error("Error removing member:", error);
      message.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  useEffect(() => {
    fetchFacilities();
  }, [isOrganizationOwner, ownedOrganizations]);

  const handleCreate = () => {
    setEditingFacility(null);
    form.resetFields();
    form.setFieldsValue({ 
      facility_status: 'Active',
      country: 'US'
    });
    setModalVisible(true);
  };

  const handleEdit = (facility: Facility) => {
    setEditingFacility(facility);
    form.setFieldsValue({
      ...facility,
      phone: facility.phone ? formatPhoneNumber(facility.phone) : facility.phone
    });
    setModalVisible(true);
  };

  // Enhanced facility operations with billing impact
  const handleCreateWithBilling = (values: FacilityFormData) => {
    if (!ownedOrganizations.length) return;

    // Calculate new facility count (always +1 for creation)
    const currentActiveFacilities = facilities.filter(f => f.facility_status === 'Active').length;
    const newActiveFacilities = values.facility_status === 'Active' ? currentActiveFacilities + 1 : currentActiveFacilities;

    // Set pending action and show billing modal
    setPendingAction({
      type: 'create',
      formData: values
    });

    setBillingModalVisible(true);
  };

  const handleDeleteWithBilling = (facilityId: string, facilityName: string) => {
    // Find the facility to determine if it's active
    const facility = facilities.find(f => f.id === facilityId);
    if (!facility) return;

    setPendingAction({
      type: 'delete',
      facilityId: facilityId
    });

    setBillingModalVisible(true);
  };

  const handleStatusChangeWithBilling = (facilityId: string, newStatus: 'Active' | 'Inactive') => {
    setPendingAction({
      type: 'status_change',
      facilityId: facilityId,
      newStatus: newStatus
    });

    setBillingModalVisible(true);
  };

  // Execute the actual operations after billing confirmation
  const executePendingAction = async () => {
    if (!pendingAction || !ownedOrganizations.length) return;

    try {
      setSubmitting(true);
      const token = await getAccessTokenSilently();
      
      if (pendingAction.type === 'create') {
        // Create new facility
        const submissionData = {
          ...pendingAction.formData,
          phone: pendingAction.formData.phone ? stripPhoneFormatting(pendingAction.formData.phone) : pendingAction.formData.phone
        };
        
        await apiClient.post(`/api/organization/${ownedOrganizations[0].id}/facilities`, submissionData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        message.success("Facility created successfully");
        setModalVisible(false);
        form.resetFields();

      } else if (pendingAction.type === 'delete') {
        // Delete facility
        await apiClient.delete(`/api/facility/${pendingAction.facilityId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        message.success("Facility archived successfully");

             } else if (pendingAction.type === 'status_change' && pendingAction.newStatus) {
         // Update facility status
         await apiClient.patch(`/api/facility/${pendingAction.facilityId}/status`, {
           facility_status: pendingAction.newStatus
         }, {
           headers: {
             Authorization: `Bearer ${token}`,
           },
         });
         
         message.success(`Facility ${pendingAction.newStatus.toLowerCase()} successfully`);
      }

      // Update subscription if needed
      await updateSubscriptionAfterFacilityChange();
      
      // Refresh facilities
      await fetchFacilities();
      
    } catch (error: any) {
      console.error("Error executing facility action:", error);
      message.error(error.response?.data?.detail || "Failed to complete action");
    } finally {
      setSubmitting(false);
      setBillingModalVisible(false);
      setPendingAction(null);
    }
  };

  const updateSubscriptionAfterFacilityChange = async () => {
    if (!ownedOrganizations.length) return;

    try {
      const token = await getAccessTokenSilently();
      const currentActiveFacilities = facilities.filter(f => f.facility_status === 'Active').length;
      
      // Calculate new count based on pending action
      let newActiveFacilities = currentActiveFacilities;
      
      if (pendingAction?.type === 'create' && pendingAction.formData?.facility_status === 'Active') {
        newActiveFacilities = currentActiveFacilities + 1;
      } else if (pendingAction?.type === 'delete') {
        const facility = facilities.find(f => f.id === pendingAction.facilityId);
        if (facility?.facility_status === 'Active') {
          newActiveFacilities = currentActiveFacilities - 1;
        }
      } else if (pendingAction?.type === 'status_change') {
        const facility = facilities.find(f => f.id === pendingAction.facilityId);
        if (facility) {
          if (facility.facility_status === 'Active' && pendingAction.newStatus === 'Inactive') {
            newActiveFacilities = currentActiveFacilities - 1;
          } else if (facility.facility_status === 'Inactive' && pendingAction.newStatus === 'Active') {
            newActiveFacilities = currentActiveFacilities + 1;
          }
        }
      }

      // Update subscription
      await FacilityBillingService.updateFacilitySubscription(
        ownedOrganizations[0].id,
        newActiveFacilities,
        token
      );

    } catch (error) {
      console.error("Error updating subscription:", error);
      // Don't fail the main operation, just log
    }
  };

  const getCurrentActiveFacilityCount = () => {
    return facilities.filter(f => f.facility_status === 'Active').length;
  };

  const getNewActiveFacilityCount = () => {
    const currentActive = getCurrentActiveFacilityCount();
    
    if (pendingAction?.type === 'create') {
      return pendingAction.formData?.facility_status === 'Active' ? currentActive + 1 : currentActive;
    } else if (pendingAction?.type === 'delete') {
      const facility = facilities.find(f => f.id === pendingAction.facilityId);
      return facility?.facility_status === 'Active' ? currentActive - 1 : currentActive;
    } else if (pendingAction?.type === 'status_change') {
      const facility = facilities.find(f => f.id === pendingAction.facilityId);
      if (facility) {
        if (facility.facility_status === 'Active' && pendingAction.newStatus === 'Inactive') {
          return currentActive - 1;
        } else if (facility.facility_status === 'Inactive' && pendingAction.newStatus === 'Active') {
          return currentActive + 1;
        }
      }
    }
    
    return currentActive;
  };

  const getBillingActionDescription = () => {
    if (!pendingAction) return "";
    
    if (pendingAction.type === 'create') {
      const facilityName = pendingAction.formData?.name || 'New Facility';
      const status = pendingAction.formData?.facility_status || 'Active';
      return `Creating "${facilityName}" with status: ${status}`;
    } else if (pendingAction.type === 'delete') {
      const facility = facilities.find(f => f.id === pendingAction.facilityId);
      return `Archiving facility "${facility?.name || 'Unknown'}"`;
    } else if (pendingAction.type === 'status_change') {
      const facility = facilities.find(f => f.id === pendingAction.facilityId);
      return `Changing "${facility?.name || 'Unknown'}" status to ${pendingAction.newStatus}`;
    }
    
    return "";
  };

  // Modified submit handler to use billing flow
  const handleSubmit = async (values: FacilityFormData) => {
    if (editingFacility) {
      // For edits, use the original direct submit if not changing status
      const isStatusChange = editingFacility.facility_status !== values.facility_status;
      
      if (isStatusChange) {
        // Status change - use billing flow
        handleStatusChangeWithBilling(editingFacility.id, values.facility_status);
      } else {
        // No status change - direct update
        try {
          setSubmitting(true);
          const token = await getAccessTokenSilently();
          
          const submissionData = {
            ...values,
            phone: values.phone ? stripPhoneFormatting(values.phone) : values.phone
          };
          
          await apiClient.put(`/api/facility/${editingFacility.id}`, submissionData, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          
          message.success("Facility updated successfully");
          setModalVisible(false);
          form.resetFields();
          await fetchFacilities();
        } catch (error: any) {
          console.error("Error updating facility:", error);
          message.error(error.response?.data?.detail || "Failed to update facility");
        } finally {
          setSubmitting(false);
        }
      }
    } else {
      // New facility - use billing flow
      handleCreateWithBilling(values);
    }
  };

  // Modified status change handler
  const handleStatusChange = async (facilityId: string, newStatus: 'Active' | 'Inactive') => {
    handleStatusChangeWithBilling(facilityId, newStatus);
  };

  const columns: ColumnsType<Facility> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
      width: 150,
    },
    {
      title: 'Address',
      key: 'address',
      width: 200,
      responsive: ['md'],
      render: (_, record) => {
        const addressParts = [
          record.street1,
          record.city,
          record.state,
          record.zip,
        ].filter(Boolean);
        
        return addressParts.length > 0 || record.phone ? (
          <Space direction="vertical" size="small">
            {addressParts.length > 0 && (
              <Text><EnvironmentOutlined /> {addressParts.join(', ')}</Text>
            )}
            {record.phone && (
              <Text><PhoneOutlined /> {formatPhoneNumber(record.phone)}</Text>
            )}
          </Space>
        ) : (
          <Text type="secondary">No address provided</Text>
        );
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 150,
      responsive: ['xs', 'sm'],
      render: (_, record) => {
        if (!record.phone && !record.city) {
          return <Text type="secondary">-</Text>;
        }
        return (
          <Space direction="vertical" size="small">
            {record.phone && (
              <Text><PhoneOutlined /> {formatPhoneNumber(record.phone)}</Text>
            )}
            {record.city && record.state && (
              <Text><EnvironmentOutlined /> {record.city}, {record.state}</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'facility_status',
      key: 'facility_status',
      width: 100,
      render: (status: string) => (
        <Tag 
          color={status === 'Active' ? 'green' : 'red'} 
          icon={status === 'Active' ? <CheckCircleOutlined /> : <StopOutlined />}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Active Users',
      key: 'active_users',
      width: 120,
      responsive: ['md'],
      render: (_, record) => {
        const activeUsers = record.active_users_count || 0;
        
        return (
          <Button
            type="text"
            onClick={() => handleManageMembers(record)}
            style={{ 
              padding: '4px 8px',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            className="facility-members-btn"
          >
            <Text strong style={{ color: '#1890ff', fontSize: '18px' }}>
              {activeUsers}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              <UserOutlined /> Active
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 100,
      responsive: ['lg'],
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => {
        const menuItems: MenuProps['items'] = [
          {
            key: 'members',
            label: 'Manage Members',
            icon: <TeamOutlined />,
            onClick: () => handleManageMembers(record),
          },
          {
            key: 'edit',
            label: 'Edit Facility',
            icon: <EditOutlined />,
            onClick: () => handleEdit(record),
          },
          {
            key: 'status',
            label: record.facility_status === 'Active' ? 'Deactivate' : 'Activate',
            icon: record.facility_status === 'Active' ? <PauseCircleOutlined /> : <PlayCircleOutlined />,
            onClick: () => handleStatusChange(
              record.id, 
              record.facility_status === 'Active' ? 'Inactive' : 'Active'
            ),
          },
          {
            type: 'divider',
          },
          {
            key: 'archive',
            label: 'Archive Facility',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Archive Facility',
                content: 'Are you sure you want to archive this facility?',
                okText: 'Yes, Archive',
                cancelText: 'Cancel',
                okButtonProps: { danger: true },
                onOk: () => handleDeleteWithBilling(record.id, record.name),
              });
            },
          },
        ];

        return (
          <Dropdown 
            menu={{ items: menuItems }} 
            trigger={['click']}
            placement="bottomRight"
          >
            <Button 
              type="text" 
              icon={<MoreOutlined />}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Dropdown>
        );
      },
    },
  ];

  if (!isOrganizationOwner) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="Only organization owners can manage facilities."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const activeFacilities = facilities.filter(f => f.facility_status === 'Active').length;
  const totalFacilities = facilities.length;

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BuildOutlined /> Facilities Management
      </Title>
      
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Total Facilities"
              value={totalFacilities}
              prefix={<BuildOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="Active Facilities"
              value={activeFacilities}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8}>
          <Card>
            <Statistic
              title="Inactive Facilities"
              value={totalFacilities - activeFacilities}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Facilities"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Facility
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={facilities}
          rowKey="id"
          loading={loading}
          scroll={{ x: 800 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            responsive: true,
            showLessItems: true,
          }}
          size="middle"
        />
      </Card>

      {/* Facility Create/Edit Modal */}
      <Modal
        title={editingFacility ? "Edit Facility" : "Create New Facility"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Facility Name"
            rules={[
              { required: true, message: 'Please enter facility name' },
              { min: 2, message: 'Name must be at least 2 characters' },
            ]}
          >
            <Input placeholder="Enter facility name" />
          </Form.Item>

          <Form.Item
            name="street1"
            label="Street Address"
          >
            <Input placeholder="Enter street address" />
          </Form.Item>

          <Form.Item
            name="street2"
            label="Street Address 2"
          >
            <Input placeholder="Apartment, suite, etc." />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="city"
                label="City"
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="state"
                label="State"
              >
                <Select placeholder="Select state" showSearch>
                  {US_STATES.map(state => (
                    <Option key={state.value} value={state.value}>
                      {state.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="zip"
                label="ZIP Code"
              >
                <Input placeholder="ZIP" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="country"
                label="Country"
                initialValue="US"
              >
                <Select placeholder="Select country">
                  <Option value="US">United States</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  {
                    pattern: /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
                    message: 'Please enter a valid US phone number (e.g., (123) 456-7890, 123-456-7890, or 1234567890)'
                  }
                ]}
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="facility_status"
            label="Status"
            rules={[{ required: true, message: 'Please select status' }]}
          >
            <Select placeholder="Select status">
              <Option value="Active">Active</Option>
              <Option value="Inactive">Inactive</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingFacility ? 'Update' : 'Create'} Facility
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Members Management Drawer */}
      <Drawer
        title={
          <Space>
            <TeamOutlined />
            <span>{selectedFacility?.name} Members</span>
            {membersData && (
              <Badge 
                count={membersData.total_count} 
                style={{ backgroundColor: '#52c41a' }}
              />
            )}
          </Space>
        }
        placement="right"
        width={600}
        open={membersDrawerVisible}
        onClose={() => setMembersDrawerVisible(false)}
        extra={
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={() => setInviteModalVisible(true)}
          >
            Invite Members
          </Button>
        }
      >
        {membersData && (
          <>
            {membersData.pending_invitations > 0 && (
              <Alert
                message={`${membersData.pending_invitations} pending invitation${membersData.pending_invitations > 1 ? 's' : ''}`}
                type="info"
                style={{ marginBottom: 16 }}
                icon={<ClockCircleOutlined />}
              />
            )}
            
            <List
              loading={membersLoading}
              dataSource={membersData.members}
              renderItem={(member) => (
                <List.Item
                  actions={[
                    member.status === 'Pending' && (
                      <Tooltip title="Copy Invitation Link">
                        <Button 
                          type="link" 
                          size="small"
                          icon={<CopyOutlined />}
                          onClick={() => handleCopyInvitationLink(member.id, member.email)}
                        />
                      </Tooltip>
                    ),
                    member.status === 'Pending' && (
                      <Tooltip title="Resend Invitation">
                        <Button 
                          type="link" 
                          size="small"
                          icon={<SendOutlined />}
                          onClick={() => handleResendInvitation(member.id, member.email)}
                        />
                      </Tooltip>
                    ),
                    member.status !== 'Pending' && (
                      <Tooltip title="Change Role">
                        <Button type="link" size="small">
                          <CrownOutlined />
                        </Button>
                      </Tooltip>
                    ),
                    <Popconfirm
                      title={`Remove ${member.name}?`}
                      description={member.status === 'Pending' ? 
                        "This will cancel the invitation." : 
                        "This will remove them from the facility."
                      }
                      onConfirm={() => handleRemoveMember(member.id, member.name)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button type="link" danger size="small">
                        <DeleteOutlined />
                      </Button>
                    </Popconfirm>
                  ].filter(Boolean)}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar 
                        icon={<UserOutlined />}
                        style={{ 
                          backgroundColor: member.status === 'Pending' ? '#faad14' : '#1890ff' 
                        }}
                      />
                    }
                    title={
                      <Space>
                        <span>{member.name}</span>
                        <Tag 
                          color={member.role === 'Owner' ? 'gold' : 'blue'}
                          icon={member.role === 'Owner' ? <CrownOutlined /> : <UserOutlined />}
                        >
                          {member.role}
                        </Tag>
                        <Tag 
                          color={
                            member.status === 'Active' ? 'green' : 
                            member.status === 'Pending' ? 'orange' : 'red'
                          }
                          icon={
                            member.status === 'Active' ? <CheckCircleOutlined /> :
                            member.status === 'Pending' ? <ClockCircleOutlined /> :
                            <ExclamationCircleOutlined />
                          }
                        >
                          {member.status}
                        </Tag>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <Text><MailOutlined /> {member.email}</Text>
                        <Text type="secondary">
                          {member.status === 'Pending' ? 
                            `Invited ${new Date(member.created_at).toLocaleDateString()}` :
                            `Joined ${new Date(member.created_at).toLocaleDateString()}`
                          }
                        </Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Drawer>

      {/* Invite Member Modal */}
      <Modal
        title="Invite Members"
        open={inviteModalVisible}
        onCancel={() => {
          setInviteModalVisible(false);
          inviteForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInviteMember}
        >
          <Form.Item
            name="emails"
            label="Email Addresses"
            rules={[
              { required: true, message: 'Please enter at least one email address' },
            ]}
          >
            <Input.TextArea 
              placeholder="Enter email addresses (one per line or separated by commas)&#10;example1@company.com&#10;example2@company.com, example3@company.com"
              rows={4}
              style={{ resize: 'vertical' }}
            />
          </Form.Item>

          <Divider />
          
          <Alert
            message="Invitation Process"
            description="All invited users will receive email invitations to create their accounts. Invitations expire in 14 days."
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setInviteModalVisible(false)}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={inviting}
                icon={<MailOutlined />}
              >
                Send Invitations
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Billing Impact Modal */}
      {pendingAction && ownedOrganizations.length > 0 && (
        <BillingImpactModal
          visible={billingModalVisible}
          onCancel={() => {
            setBillingModalVisible(false);
            setPendingAction(null);
          }}
          onConfirm={executePendingAction}
          organizationId={ownedOrganizations[0].id}
          currentFacilityCount={getCurrentActiveFacilityCount()}
          newFacilityCount={getNewActiveFacilityCount()}
          actionType={pendingAction.type === 'create' ? 'add' : 'remove'}
          actionDescription={getBillingActionDescription()}
          confirmLoading={submitting}
        />
      )}
    </div>
  );
}; 